import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  ListChecks,
  Save,
  ShieldCheck,
  X,
} from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Input from "../../../components/Input/Input";
import Loader from "../../../components/Loader/Loader";
import Notification from "../../../components/Notification/Notification";

/* ===================== SERVICES & UTILS ===================== */
import { apiGet, apiPost, apiPut } from "../../../apiService";
import { getErrorMessage } from "../../../utils/errorHelper";

/* ===================== STYLES ===================== */
import styles from "./RoleForm.module.scss";

export const ROLES_BASE_PATH = "/admin/role-permission";

const ROLE_TYPES = ["Internal", "Client"];

/* "add-los-creds" → "Add LOS Creds", "api-health" → "API Health" */
const ACRONYMS = { api: "API", los: "LOS" };
const humanizeAction = (slug) =>
  String(slug)
    .split("-")
    .map(
      (word) =>
        ACRONYMS[word.toLowerCase()] ||
        word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");

/* Dashboard "view" is mandatory — every role must at least see the dashboard. */
const isLockedAction = (module, action) =>
  module.slug === "dashboard" && action === "view";

const RoleForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [roleName, setRoleName] = useState("");
  const [roleNameError, setRoleNameError] = useState("");
  const [roleType, setRoleType] = useState("Internal");
  const [permVersion, setPermVersion] = useState(null);

  /* Normalized module list: { id, slug, name, availableActions } */
  const [modules, setModules] = useState([]);
  /* Selected actions per module: { [moduleId]: ["view", "update", ...] } */
  const [selected, setSelected] = useState({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const notify = useCallback((message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3500);
  }, []);

  /* Pre-select the mandatory dashboard view */
  const seedDashboard = (mods) => {
    const seeded = {};
    mods.forEach((m) => {
      if (m.slug === "dashboard" && m.availableActions.includes("view")) {
        seeded[m.id] = ["view"];
      }
    });
    return seeded;
  };

  /* -------- CREATE: modules from the sidebar endpoint -------- */
  const fetchModules = useCallback(
    async (type) => {
      setLoading(true);
      try {
        const res = await apiGet("user", `/modules/sidebar?userType=${type}`);
        const mods = (Array.isArray(res?.data) ? res.data : []).map((m) => ({
          id: m.id,
          slug: m.slug,
          name: m.name,
          availableActions: m.actions || [],
        }));
        setModules(mods);
        setSelected(seedDashboard(mods));
      } catch (error) {
        notify(getErrorMessage(error, "Failed to load modules"), "error");
      } finally {
        setLoading(false);
      }
    },
    [notify],
  );

  /* -------- EDIT: role details carry the modules + selections -------- */
  const fetchRole = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet("user", `/roles/${id}`);
      const role = res?.data;
      if (!role?.id) {
        throw new Error(
          JSON.stringify({ message: res?.message || "Role not found" }),
        );
      }

      setRoleName(role.name || "");
      setRoleType(role.roleType || "Internal");
      setPermVersion(role.permVersion ?? null);

      const mods = (role.modules || []).map((m) => ({
        id: m.moduleId,
        slug: m.moduleSlug,
        name: m.moduleName,
        availableActions: m.availableActions || [],
      }));
      setModules(mods);

      const sel = {};
      (role.modules || []).forEach((m) => {
        const actions = Array.isArray(m.actions) ? [...m.actions] : [];
        if (
          m.moduleSlug === "dashboard" &&
          (m.availableActions || []).includes("view") &&
          !actions.includes("view")
        ) {
          actions.unshift("view");
        }
        sel[m.moduleId] = actions;
      });
      setSelected(sel);
    } catch (error) {
      notify(getErrorMessage(error, "Failed to load role details"), "error");
    } finally {
      setLoading(false);
    }
  }, [id, notify]);

  useEffect(() => {
    if (isEdit) fetchRole();
  }, [isEdit, fetchRole]);

  /* Create mode: (re)load the module list whenever the role type changes */
  useEffect(() => {
    if (!isEdit) fetchModules(roleType);
  }, [isEdit, roleType, fetchModules]);

  /* -------- selection handlers -------- */
  const toggleAction = (module, action) => {
    if (isLockedAction(module, action)) return;
    setSelected((prev) => {
      const current = prev[module.id] || [];
      return {
        ...prev,
        [module.id]: current.includes(action)
          ? current.filter((a) => a !== action)
          : [...current, action],
      };
    });
  };

  const isAllSelected = (module) =>
    module.availableActions.length > 0 &&
    module.availableActions.every((a) =>
      (selected[module.id] || []).includes(a),
    );

  const toggleAll = (module) => {
    setSelected((prev) => ({
      ...prev,
      [module.id]: isAllSelected(module)
        ? module.slug === "dashboard" &&
          module.availableActions.includes("view")
          ? ["view"]
          : []
        : [...module.availableActions],
    }));
  };

  /* -------- save -------- */
  const handleSave = async () => {
    if (!roleName.trim()) {
      setRoleNameError("Role name is required");
      return;
    }

    setSaving(true);
    try {
      const modulesPayload = modules.map((m) => ({
        module_id: m.id,
        moduleId: m.id,
        actions: selected[m.id] || [],
      }));

      if (isEdit) {
        await apiPut("user", `/roles/${id}`, {
          name: roleName.trim(),
          roleType,
          ...(permVersion != null && { permVersion }),
          modules: modulesPayload,
        });
      } else {
        await apiPost("user", "/roles", {
          name: roleName.trim(),
          roleType,
          permissions: modulesPayload,
        });
      }

      navigate(ROLES_BASE_PATH, {
        state: {
          notice: isEdit
            ? "Role updated successfully"
            : "Role created successfully",
        },
      });
    } catch (error) {
      console.error("SAVE ROLE ERROR:", error);
      notify(getErrorMessage(error, "Failed to save role"), "error");
      setSaving(false);
    }
  };

  /* ===================== JSX ===================== */
  return (
    <div className={styles["role-form__page"]}>
      {notification && (
        <div className={styles["role-form__toast"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER */}
      <div className={styles["role-form__header"]}>
        <button
          type="button"
          className={styles["role-form__back"]}
          onClick={() => navigate(ROLES_BASE_PATH)}
          data-tooltip="Back to Roles & Permissions"
        >
          <ArrowLeft size={20} strokeWidth={2.5} />
        </button>
        <div className={styles["role-form__icon-wrapper"]}>
          <ShieldCheck size={24} strokeWidth={2.5} />
        </div>
        <div className={styles["role-form__title-group"]}>
          <h2 className={styles["role-form__title"]}>
            {isEdit ? "Edit Role" : "Add New Role"}
          </h2>
          <p className={styles["role-form__subtitle"]}>
            {isEdit
              ? "Modify the role details and access permissions."
              : "Create a new role and define its system permissions."}
          </p>
        </div>
      </div>

      {loading ? (
        <div className={styles["role-form__card"]}>
          <Loader />
        </div>
      ) : (
        <form
          className={styles["role-form__form"]}
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          {/* ROLE DETAILS */}
          <div className={styles["role-form__card"]}>
            <div className={styles["role-form__card-header"]}>
              <div className={styles["role-form__card-icon"]}>
                <ShieldCheck size={18} strokeWidth={2.5} />
              </div>
              <h3 className={styles["role-form__card-title"]}>Role Details</h3>
            </div>

            <div className={styles["role-form__grid"]}>
              <Input
                label={
                  <>
                    <span>Role Name</span>
                    <span style={{ color: "red", marginLeft: "2px" }}>*</span>
                  </>
                }
                placeholder="e.g., Content Manager"
                value={roleName}
                onChange={(e) => {
                  setRoleName(e.target.value);
                  setRoleNameError("");
                }}
                error={roleNameError}
              />

              <Input
                type="select"
                label={
                  <>
                    <span>Role Type</span>
                    <span style={{ color: "red", marginLeft: "2px" }}>*</span>
                  </>
                }
                value={roleType}
                onChange={(e) => setRoleType(e.target.value)}
                options={ROLE_TYPES.map((t) => ({ label: t, value: t }))}
                disabled={isEdit}
              />
            </div>
            {!isEdit && (
              <p className={styles["role-form__hint"]}>
                Changing the role type reloads the module list for that user
                type.
              </p>
            )}
          </div>

          {/* PERMISSIONS */}
          <div className={styles["role-form__card"]}>
            <div className={styles["role-form__card-header"]}>
              <div className={styles["role-form__card-icon"]}>
                <ListChecks size={18} strokeWidth={2.5} />
              </div>
              <h3 className={styles["role-form__card-title"]}>
                Screen Access &amp; Permissions
              </h3>
            </div>

            {modules.length === 0 ? (
              <p className={styles["role-form__hint"]}>No modules available.</p>
            ) : (
              <div className={styles["role-form__module-list"]}>
                {modules.map((module) => {
                  const selectedCount = (selected[module.id] || []).length;
                  return (
                    <div
                      className={styles["role-form__module-card"]}
                      key={module.id}
                    >
                      <div className={styles["role-form__module-head"]}>
                        <div className={styles["role-form__module-info"]}>
                          <span className={styles["role-form__module-name"]}>
                            {module.name}
                          </span>
                          <span className={styles["role-form__module-count"]}>
                            {selectedCount}/{module.availableActions.length}{" "}
                            selected
                          </span>
                        </div>

                        <label className={styles["role-form__checkbox"]}>
                          <input
                            type="checkbox"
                            checked={isAllSelected(module)}
                            onChange={() => toggleAll(module)}
                          />
                          <div className={styles["role-form__checkmark"]}>
                            <Check size={14} strokeWidth={3} />
                          </div>
                          <span className={styles["role-form__label-text"]}>
                            Select all
                          </span>
                        </label>
                      </div>

                      <div className={styles["role-form__actions-grid"]}>
                        {module.availableActions.map((action) => {
                          const locked = isLockedAction(module, action);
                          const checked =
                            locked ||
                            (selected[module.id] || []).includes(action);
                          return (
                            <label
                              className={styles["role-form__checkbox"]}
                              key={`${module.id}-${action}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={locked}
                                onChange={() => toggleAction(module, action)}
                              />
                              <div className={styles["role-form__checkmark"]}>
                                <Check size={14} strokeWidth={3} />
                              </div>
                              <span
                                className={styles["role-form__label-text"]}
                              >
                                {humanizeAction(action)}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ACTIONS */}
          <div className={styles["role-form__actions"]}>
            <button
              type="button"
              className={`${styles["role-form__btn"]} ${styles["role-form__btn--secondary"]}`}
              onClick={() => navigate(ROLES_BASE_PATH)}
              disabled={saving}
            >
              <X size={16} strokeWidth={2.5} />
              Cancel
            </button>
            <button
              type="submit"
              className={`${styles["role-form__btn"]} ${styles["role-form__btn--primary"]}`}
              disabled={saving}
            >
              <Save size={16} strokeWidth={2.5} />
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Role"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default RoleForm;
